<script setup lang="ts">
import IconCircleCheck from '~icons/tabler/circle-check';
import IconAlertTriangle from '~icons/tabler/alert-triangle';
import BtnCta from '@/components/buttons/BtnCta.vue';

defineOptions({name: 'BaseNotificationPopup'});

withDefaults(defineProps<{
    variant?: 'loading' | 'success' | 'error';
    title: string;
    /**
     * Omit to render no button — for a popup the app dismisses itself once the
     * work it reports finishes, which is the whole point of the loading variant.
     */
    actionLabel?: string;
}>(), {
    variant: 'loading',
    actionLabel: '',
});

defineEmits<{
    (e: 'action'): void;
}>();
</script>

<template>
    <div
        class="merit-notification-popup"
        :role="variant === 'error' ? 'alert' : 'status'"
        :aria-busy="variant === 'loading' ? 'true' : undefined"
    >
        <div class="merit-notification-popup__graphic">
            <!-- "Chase" from 10015.io's CSS loader generator, the loader the design annotates
                 (#2EB336, medium, average speed). Six dots orbit a rotating container, each
                 pulsing on its own offset. -->
            <span v-if="variant === 'loading'" class="merit-notification-popup__chase" aria-hidden="true">
                <span v-for="dot in 6" :key="dot" class="merit-notification-popup__chase-dot"></span>
            </span>
            <IconCircleCheck
                v-else-if="variant === 'success'"
                class="merit-notification-popup__icon merit-notification-popup__icon--success"
                aria-hidden="true"
            />
            <IconAlertTriangle
                v-else
                class="merit-notification-popup__icon merit-notification-popup__icon--error"
                aria-hidden="true"
            />
        </div>

        <div class="merit-notification-popup__content">
            <div class="merit-notification-popup__text">
                <p class="merit-notification-popup__title">{{ title }}</p>
                <p class="merit-notification-popup__body"><slot /></p>
            </div>
            <BtnCta v-if="actionLabel" data-test="notification-popup-action" @click="$emit('action')">
                {{ actionLabel }}
            </BtnCta>
        </div>
    </div>
</template>

<style scoped>
/* merit- prefix keeps these off the legacy global rules for generic names like .popup. */
.merit-notification-popup {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    width: 400px;
    /* min-height rather than height: the design is a 400px square, but a title that wraps to three
       lines should push the card taller instead of being clipped by its overflow. */
    min-height: 400px;
    padding: 20px 32px 32px;
    background: #fff;
    border-radius: 4px;
    font-family: Inter, sans-serif;
    color: #292929;
}

.merit-notification-popup__graphic {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    height: 100px;
    /* The graphic sits 7px left of the text block in the design — the glyph is optically aligned
       rather than box-aligned. */
    margin-left: -7px;
}

.merit-notification-popup__icon {
    width: 100px;
    height: 100px;
}

.merit-notification-popup__icon--success {
    color: #2EB336;
}

.merit-notification-popup__icon--error {
    color: #F74C4C;
}

.merit-notification-popup__chase {
    position: relative;
    width: 40px;
    height: 40px;
    animation: merit-chase 2.5s linear infinite both;
}

.merit-notification-popup__chase-dot {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    animation: merit-chase-dot 2s ease-in-out infinite both;
}

.merit-notification-popup__chase-dot::before {
    content: "";
    display: block;
    width: 25%;
    height: 25%;
    background: #2EB336;
    border-radius: 50%;
    animation: merit-chase-dot-before 2s ease-in-out infinite both;
}

/* Each dot trails the one before it by 0.1s, which is what turns six pulsing dots into one
   chasing ring. The dot and its ::before share a delay so the orbit and the pulse stay in step. */
.merit-notification-popup__chase-dot:nth-child(1),
.merit-notification-popup__chase-dot:nth-child(1)::before { animation-delay: -1.1s; }
.merit-notification-popup__chase-dot:nth-child(2),
.merit-notification-popup__chase-dot:nth-child(2)::before { animation-delay: -1s; }
.merit-notification-popup__chase-dot:nth-child(3),
.merit-notification-popup__chase-dot:nth-child(3)::before { animation-delay: -0.9s; }
.merit-notification-popup__chase-dot:nth-child(4),
.merit-notification-popup__chase-dot:nth-child(4)::before { animation-delay: -0.8s; }
.merit-notification-popup__chase-dot:nth-child(5),
.merit-notification-popup__chase-dot:nth-child(5)::before { animation-delay: -0.7s; }
.merit-notification-popup__chase-dot:nth-child(6),
.merit-notification-popup__chase-dot:nth-child(6)::before { animation-delay: -0.6s; }

@keyframes merit-chase {
    100% { transform: rotate(360deg); }
}

@keyframes merit-chase-dot {
    80%, 100% { transform: rotate(360deg); }
}

@keyframes merit-chase-dot-before {
    50% { transform: scale(0.4); }
    0%, 100% { transform: scale(1); }
}

/* Spinning dots are exactly what vestibular triggers look like; hold the ring still and let the
   dots pulse so the popup still reads as "working". */
@media (prefers-reduced-motion: reduce) {
    .merit-notification-popup__chase {
        animation: none;
    }

    .merit-notification-popup__chase-dot {
        animation: none;
    }
}

.merit-notification-popup__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
}

.merit-notification-popup__text {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.merit-notification-popup__title {
    margin: 0;
    font-size: 26px;
    font-weight: 500;
    line-height: 32px;
}

.merit-notification-popup__body {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    font-feature-settings: "lnum" 1, "tnum" 1;
}
</style>
